import type { MapDocument, MapTriggerVolume } from './content/schema/map';

export interface AuditIssue {
  id: string;
  severity: 'error' | 'warning' | 'info';
  category: 'spawns' | 'lighting' | 'topology' | 'prefabs';
  title: string;
  description: string;
  fixHint?: string;
}

export interface MapAuditReport {
  score: number; // 0..100
  passed: boolean;
  issues: AuditIssue[];
}

/** Every distinct team a spawn marker on this map declares. */
function spawnTeams(doc: MapDocument): string[] {
  const teams = new Set<string>();
  for (const marker of doc.markers ?? []) {
    if (marker.kind !== 'spawn') continue;
    const team = marker.metadata?.['team'];
    if (typeof team === 'string') teams.add(team);
  }
  return [...teams];
}

/** Every distinct objective site an 'event' trigger declares. */
function objectiveSites(doc: MapDocument): Set<string> {
  const sites = new Set<string>();
  for (const trigger of (doc.triggers ?? []) as MapTriggerVolume[]) {
    if (trigger.kind !== 'event') continue;
    const site = trigger.params?.['site'];
    if (site != null) sites.add(String(site));
  }
  return sites;
}

/**
 * Pre-flight readiness score for a map, ahead of publishing it.
 *
 * Deliberately game-agnostic: every check here reads the map document's own
 * generic vocabulary (marker `kind`/`metadata`, trigger `kind`/`params`) and
 * self-gates on evidence the map actually wants that kind of content, rather
 * than assuming any one project's asset ids or rules. A map that authors no
 * spawn markers or objective triggers at all - which describes most non-Sunder
 * projects - triggers neither check and gets a clean report, not a false
 * "missing raider spawns" failure for a game that has no raiders.
 *
 * Sunder's own hard, blocking rules (which team must exist, exactly which
 * modes need bomb sites) live separately in `mapLinter.ts`'s
 * `SUNDER_MISSING_TEAM_SPAWN`/`SUNDER_NO_BOMB_SITES` checks, gated the same
 * way and enforced identically by `@sstk/sunder-sim` at load. This audit is
 * the softer, advisory sibling - a readiness score, not a save-blocking gate.
 */
export function auditMapDocument(doc: MapDocument): MapAuditReport {
  const issues: AuditIssue[] = [];
  const instances = doc.objectInstances ?? [];

  // 1. Spawn balance. Only fires once the map has committed to team-tagged
  // spawns at all - a single-player or non-team project simply has none of
  // these markers and is left alone.
  const teams = spawnTeams(doc);
  if (teams.length === 1) {
    issues.push({
      id: 'single-team-spawns',
      severity: 'warning',
      category: 'spawns',
      title: 'Only One Team Has Spawn Points',
      description: `Spawn markers exist for "${teams[0]}" only. A team-based mode needs spawns for every side.`,
      fixHint: 'Add a spawn marker for the other team, with its Team metadata set.',
    });
  }

  // 2. Objective site completeness. Only fires once the map has already
  // placed at least one site-tagged event trigger - a project with no
  // objective-site concept never trips this.
  const sites = objectiveSites(doc);
  if (sites.size > 0 && sites.size < 2) {
    issues.push({
      id: 'insufficient-objective-sites',
      severity: 'warning',
      category: 'topology',
      title: 'Fewer than 2 Objective Sites',
      description: `Map currently declares ${sites.size} objective site(s) via event triggers. Multi-site objective modes typically expect at least two.`,
      fixHint: 'Add another Event trigger with a distinct Site value.',
    });
  }

  // 3. Lighting budget. Already generic - any `light_*` asset, no per-game
  // vocabulary involved.
  const lights = instances.filter((i) => i.assetId.startsWith('light_'));
  if (lights.length > 16) {
    issues.push({
      id: 'high-light-count',
      severity: 'warning',
      category: 'lighting',
      title: 'High Point Light Count',
      description: `Map contains ${lights.length} active lights. High light density may degrade WebGL frame rates on low-end hardware.`,
      fixHint: 'Combine close point lights or increase individual intensity.',
    });
  } else if (lights.length === 0) {
    issues.push({
      id: 'no-lights',
      severity: 'info',
      category: 'lighting',
      title: 'No Custom Lights Configured',
      description: 'Map relies solely on global ambient directional sun lighting.',
      fixHint: 'Add point lights or a lighting prefab for depth.',
    });
  }

  // Calculate Map Readiness Score
  const errors = issues.filter((i) => i.severity === 'error').length;
  const warnings = issues.filter((i) => i.severity === 'warning').length;
  const score = Math.max(0, 100 - errors * 35 - warnings * 10);

  return {
    score,
    passed: errors === 0,
    issues,
  };
}
